import Promise from 'bluebird';
import { push } from 'react-router-redux';
import SubmissionError from 'redux-form/lib/SubmissionError';
import reduxFormActions from 'redux-form/lib/actions';
import { stopSubmit, startSubmit, change } from 'redux-form';
import unescaper from 'anchor-server-common/utilities/unescaper';
import { COLORS } from 'anchor-server-common/utilities/episode/constants';
import { updatePlaybackPositionInMs } from '../../../playbackPosition';
import {
  msToHMS,
  uploadImageData,
  getSeekAudioIndexFromEpisodeAudiosAndPositionInMs,
  MAX_FILE_SIZE,
  getV3ShareEmbedHtml,
  getV3ShareUrl,
  getIsAudioAdSlot,
} from '../../../utils';
import {
  nextFrame,
  getBlobUrlCreator,
} from '../../../../helpers/serverRenderingUtils';
import { updateAudios } from '../../../sourceAudio';
import { unsetAndRedirectUser } from '../../../user';
import AnchorAPI, { AnchorAPIError } from '../../../modules/AnchorAPI';
import { duckOperations as globalPodcastDuckOperations } from '../../../store/global/podcast';
import Decoder from '../../../modules/Decoder';
import { duckOperations as preDistributionModalScreenDuckOperations } from '../../PreDistributionModalScreen/duck';

const { SponsorshipsDecoder } = Decoder;

export const RECEIVE_EPISODE_FETCH_REQUEST =
  '@@episodeEditorScreen/RECEIVE_EPISODE_FETCH_REQUEST';
export const ADD_AUDIO_TO_EPISODE =
  '@@episodeEditorScreen/ADD_AUDIO_TO_EPISODE';
export const DISMISS_AUDIO_ERROR = '@@episodeEditorScreen/DISMISS_AUDIO_ERROR';
export const REPLACE_AUDIO_IN_EPISODE =
  '@@episodeEditorScreen/REPLACE_AUDIO_IN_EPISODE';
export const REMOVE_AUDIO_IN_EPISODE =
  '@@episodeEditorScreen/REMOVE_AUDIO_IN_EPISODE';
export const END_EPISODE_AUDIO = '@@episodeEditorScreen/END_EPISODE_AUDIO';
export const RECEIVE_PODCAST_EPISODE =
  '@@episodeEditorScreen/RECEIVE_PODCAST_EPISODE';
export const RESET_PODCAST_EPISODE =
  '@@episodeEditorScreen/RESET_PODCAST_EPISODE';
export const CHANGE_EPISODE_SORT = '@@episodeEditorScreen/CHANGE_EPISODE_SORT';
export const CHANGE_EPISODE_IMAGE =
  '@@episodeEditorScreen/CHANGE_EPISODE_IMAGE';
export const RECEIVE_EPISODE_AUDIO_CAPTION =
  '@@episodeEditorScreen/CHANGE_EPISODE_AUDIO_CAPTION';
export const RECEIVE_ACTIVE_EPISODE_AUDIO_INDEX =
  '@@episodeEditorScreen/RECEIVE_ACTIVE_EPISODE_AUDIO_INDEX';
export const RECEIVE_ACTIVE_EDITOR_AUDIO_INDEX =
  '@@episodeEditorScreen/RECEIVE_ACTIVE_EDITOR_AUDIO_ID';
export const RECEIVE_FILE_PROCESSED_PERCENT =
  '@@episodeEditorScreen/RECEIVE_FILE_PROCESSED_PERCENT';
export const RECEIVE_DELETE_SEGMENT_PROMPT =
  '@@episodeEditorScreen/RECEIVE_DELETE_SEGMENT_PROMPT';
export const RECEIVE_NEW_AUDIO_POSITION =
  '@@episodeEditorScreen/RECEIVE_NEW_AUDIO_POSITION';
export const SET_IS_HOVERING_OVER_ADD_BUTTON =
  '@@episodeEditorScreen/SET_IS_HOVERING_OVER_ADD_BUTTON';
export const SET_IS_DRAFT = '@@episodeEditorScreen/SET_IS_DRAFT';
export const SET_IS_PLAYING = '@@episodeEditorScreen/SET_IS_PLAYING';
export const SET_IS_UPDATING = '@@episodeEditorScreen/SET_IS_UPDATING';
export const SET_AWAITING_AUTO_SAVE =
  '@@episodeEditorScreen/SET_AWAITING_AUTO_SAVE';
export const SET_DO_ABANDON_CHANGES =
  '@@episodeEditorScreen/SET_DO_ABANDON_CHANGES';
export const EPISODE_EDIT_FORM = 'episodeEditorScreen';
export const EPISODE_METADATA_EDIT_FORM = 'episodeMetadataEditor';
export const RECEIVE_LOCAL_EPISODE_IMAGE_EDIT =
  '@@episodeEditorScreen/RECEIVE_LOCAL_EPISODE_IMAGE_EDIT';
export const STOP_EDITOR_AUDIO = '@@episodeEditorScreen/STOP_EDITOR_AUDIO';
export const SET_IS_USING_SHORTCUT_UPLOAD_FLOW =
  '@@episodeEditorScreen/SET_IS_USING_SHORTCUT_UPLOAD_FLOW';
export const SET_IS_AD_STATE_ENABLED =
  '@@episodeEditorScreen/SET_IS_AD_STATE_ENABLED';
export const SET_IS_SHOWING_ANCHOR_FIRST_AD_MODAL =
  '@@episodeEditorScreen/SET_IS_SHOWING_ANCHOR_FIRST_AD_MODAL';
export const SET_AUTO_PAIRED_CAMPAIGN =
  '@@episodeEditorScreen/SET_AUTO_PAIRED_CAMPAIGN';
export const SET_IS_SHOWING_ACTIVATE_SPONSORSHIPS_CONFIRMATION_MODAL =
  '@@episodeEditorScreen/SET_IS_SHOWING_ACTIVATE_SPONSORSHIPS_CONFIRMATION_MODAL';
export const SET_IS_SHOWING_SPONSORSHIPS_MUSIC_ERROR_MODAL =
  '@@episodeEditorSreen/SET_IS_SHOWING_SPONSORSHIPS_MUSIC_ERROR_MODAL';
export const SET_IS_SHOWING_PUBLISH_NEW_EPISODE_SCREEN =
  '@@episodeEditorScreen/SET_IS_SHOWING_PUBLISH_NEW_EPISODE_SCREEN';

export const PUBLISHING_STATES = {
  IS_DRAFT: 'isDraft',
  IS_SCHEDULED: 'isScheduled',
  IS_PUBLISHED: 'isPublished',
};

export const DRAG_TYPES = {
  EXISTING_AUDIO: 'existingAudio',
  NEW_AUDIO: 'newAudio',
};

const ACTIVE_EMPTY_AD_SLOT_FAKE_DURATION = 30 * 1000;

const emptyArray = [];
const emptyObject = {};

// TODO (bui): fetching states should not default to true
const initialState = {
  isFetchingEpisode: false,
  activeEpisodeAudio: null,
  activeEpisodeAudioIndex: -1,
  addAudioError: null,
  doPublishExternally: false,
  episodeAudios: emptyArray, // { url, audioId, podcastEpisodeAudioId, isUploading}
  episodeDuration: 0,
  episodeDurationHMS: null,
  fileProcessedPercent: null,
  awaitingAutoSave: null,
  isDraft: true,
  isPlaying: false,
  isUpdating: false,
  metadata: emptyObject,
  playedDuration: 0,
  podcastId: null,
  podcastEpisodeId: null,
  editedImage: null,
  publishOn: null,
  requestDeleteAudioId: null,
  shareEmbedHtml: null,
  shareUrl: null,
  doAbandonChanges: false,
  updatingEpisode: false,
  isHoveringOverAddButton: false,
  isUsingShortcutUploadFlow: false,
  isShowingAnchorFirstAdModal: false,
  autoPairedCampaign: null,
  isShowingActivateSponsorshipsConfirmationModal: false,
  isShowingSponsorshipsMusicErrorModal: false,
  status: emptyObject,
  isShowingPublishNewEpisodeScreen: false,
};

// reducers

function episodeAudioReducer(state = emptyObject, action) {
  switch (action.type) {
    case CHANGE_EPISODE_SORT: {
      const { startSort, endSort } = action.payload;
      const { sort } = state;
      if (sort === startSort) {
        return {
          ...state,
          sort: endSort,
        };
      }
      if (startSort < endSort && sort <= endSort && sort > startSort) {
        return {
          ...state,
          sort: sort - 10,
        };
      }
      if (startSort > endSort && sort >= endSort && sort < startSort) {
        return {
          ...state,
          sort: sort + 10,
        };
      }
      return state;
    }
    case RECEIVE_PODCAST_EPISODE: {
      const { duration, caption, isAdSlotEnabled } = state;
      const audio = {
        durationHMS: msToHMS(duration),
        ...state,
      };
      if (caption) {
        audio.caption = unescaper(caption);
      } else {
        audio.caption = 'Untitled';
      }
      // enabled ad without a dynamically insertable audio
      if (isAdSlotEnabled && !audio.duration) {
        // visually green duration
        audio.duration = audio.duration || ACTIVE_EMPTY_AD_SLOT_FAKE_DURATION;
      }
      return audio;
    }
    case RECEIVE_EPISODE_AUDIO_CAPTION: {
      const { key, caption } = action.payload;
      if (state.key !== key) {
        return state;
      }
      return {
        ...state,
        caption,
      };
    }
    case REPLACE_AUDIO_IN_EPISODE: {
      const { url } = state;
      const { urlToReplace } = action.payload;
      const { audio } = action.payload;
      if (url !== urlToReplace) {
        return state;
      }
      return {
        ...state,
        ...audio,
      };
    }
    case RECEIVE_LOCAL_EPISODE_IMAGE_EDIT: {
      return {
        ...state,
        editedImage: action.payload.imageUrl,
      };
    }
    case SET_IS_AD_STATE_ENABLED: {
      if (getIsAudioAdSlot(state)) {
        const isAdSlotEnabled = action.payload;
        return {
          ...state,
          isAdSlotEnabled,
        };
      }
      return state;
    }
    default:
      return state;
  }
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_IS_SHOWING_ANCHOR_FIRST_AD_MODAL: {
      const { isShowingAnchorFirstAdModal } = action.payload;
      return {
        ...state,
        isShowingAnchorFirstAdModal,
      };
    }
    case SET_IS_SHOWING_PUBLISH_NEW_EPISODE_SCREEN: {
      const { isShowingPublishNewEpisodeScreen } = action.payload;
      return {
        ...state,
        isShowingPublishNewEpisodeScreen,
      };
    }
    case SET_IS_SHOWING_SPONSORSHIPS_MUSIC_ERROR_MODAL: {
      const { isShowingSponsorshipsMusicErrorModal } = action.payload;
      return {
        ...state,
        isShowingSponsorshipsMusicErrorModal,
      };
    }
    case SET_IS_SHOWING_ACTIVATE_SPONSORSHIPS_CONFIRMATION_MODAL: {
      const { isShowingActivateSponsorshipsConfirmationModal } = action.payload;
      return {
        ...state,
        isShowingActivateSponsorshipsConfirmationModal,
      };
    }
    case SET_AUTO_PAIRED_CAMPAIGN: {
      const { autoPairedCampaign } = action.payload;
      return {
        ...state,
        autoPairedCampaign,
      };
    }
    // (TODO: bui) use index to correctly calculate sort
    case ADD_AUDIO_TO_EPISODE: {
      const { episodeAudios, episodeDuration } = state;
      if (action.error) {
        return {
          ...state,
          addAudioError: action.payload,
        };
      }
      const { audio, idx } = action.payload;
      const { audioId, caption, duration } = audio;
      const newEpisodeDuration = episodeDuration + duration;
      const newAudios = [];
      let curSort = 0;
      const insertPosition =
        (idx !== undefined && idx) || idx === 0 ? idx : episodeAudios.length;
      for (let i = 0; i <= episodeAudios.length; i++) {
        if (insertPosition === i) {
          newAudios.push({
            ...audio,
            caption: caption || 'Untitled',
            sort: curSort,
            key: `${audioId}-${curSort}`,
          });
          curSort = curSort + 10;
        }
        if (episodeAudios[i]) {
          newAudios.push(
            Object.assign({}, episodeAudios[i], { sort: curSort })
          );
          curSort = curSort + 10;
        }
      }
      return {
        ...state,
        addAudioError: null,
        fileProcessedPercent: null,
        episodeAudios: newAudios,
        episodeDuration: newEpisodeDuration,
        episodeDurationHMS: msToHMS(newEpisodeDuration),
      };
    }
    case REPLACE_AUDIO_IN_EPISODE: {
      const { episodeAudios } = state;
      const newEpisodeAudios = episodeAudios.map(audio =>
        episodeAudioReducer(audio, action)
      );
      const newEpisodeDuration = newEpisodeAudios.reduce(
        (sum, audio) => sum + audio.duration,
        0
      );
      return {
        ...state,
        addAudioError: null,
        fileProcessedPercent: null,
        episodeAudios: newEpisodeAudios,
        episodeDuration: newEpisodeDuration,
        episodeDurationHMS: msToHMS(newEpisodeDuration),
      };
    }
    case REMOVE_AUDIO_IN_EPISODE: {
      const { audioIndex } = action.payload;
      const { episodeAudios } = state;
      const splicedAudios = episodeAudios.filter(
        (ele, idx) => idx !== audioIndex
      );
      const newEpisodeDuration = splicedAudios.reduce(
        (sum, audio) => sum + audio.duration,
        0
      );
      return {
        ...state,
        episodeAudios: splicedAudios,
        episodeDuration: newEpisodeDuration,
        episodeDurationHMS: msToHMS(newEpisodeDuration),
        activeEpisodeAudio: null,
        activeEpisodeAudioIndex: -1,
        isPlaying: false,
        playedDuration: 0,
      };
    }
    case SET_IS_AD_STATE_ENABLED: {
      return {
        ...state,
        episodeAudios: state.episodeAudios.map(a =>
          episodeAudioReducer(a, action)
        ),
      };
    }
    case END_EPISODE_AUDIO: {
      const { activeEpisodeAudioIndex, episodeAudios } = state;
      const nextActiveIndex = getNextActiveIndex(
        activeEpisodeAudioIndex,
        episodeAudios
      );
      const nextActiveAudio = episodeAudios[nextActiveIndex];
      return {
        ...state,
        activeEpisodeAudioIndex: nextActiveIndex,
        activeEpisodeAudio: nextActiveAudio,
        isPlaying: !!nextActiveAudio,
        playedDuration: playedDurationFromEpisodeAudiosAndActiveIndex(
          episodeAudios,
          nextActiveIndex
        ),
      };
    }
    case RESET_PODCAST_EPISODE: {
      return {
        ...state,
        ...initialState,
      };
    }
    case DISMISS_AUDIO_ERROR: {
      return {
        ...state,
        addAudioError: null,
      };
    }
    case RECEIVE_PODCAST_EPISODE: {
      const {
        doPublishExternally,
        episodeAudios,
        isDraft,
        metadata,
        podcastId,
        podcastEpisodeId,
        publishOn,
        status,
      } = action.payload;
      const episodeDuration = episodeDurationFromEpisodeAudios(episodeAudios);
      const durationFromAdSlots = episodeDurationFromEpisodeAudios(
        episodeAudios.filter(getIsAudioAdSlot)
      );
      const mappedEpisodeAudios = episodeAudios.map(a =>
        episodeAudioReducer(a, action)
      );
      // when updating/creating, status and metadata are unset and should not override
      return {
        ...state,
        doPublishExternally,
        episodeAudios: mappedEpisodeAudios,
        episodeDuration,
        episodeDurationHMS: msToHMS(episodeDuration - durationFromAdSlots),
        isDraft,
        metadata: {
          ...state.metadata,
          ...metadata,
        },
        podcastEpisodeId,
        podcastId,
        isFetchingEpisode: false,
        publishOn: publishOn ? new Date(publishOn) : null,
        shareEmbedHtml: getV3ShareEmbedHtml(action.payload),
        shareUrl: getV3ShareUrl(action.payload),
        status: {
          ...state.status,
          ...status,
        },
      };
    }
    case CHANGE_EPISODE_SORT: {
      const { episodeAudios } = state;
      return {
        ...state,
        episodeAudios: episodeAudios
          .map(a => episodeAudioReducer(a, action))
          .sort((a, b) => a.sort - b.sort),
        activeEpisodeAudioIndex: -1,
        isPlaying: false,
      };
    }
    case RECEIVE_EPISODE_AUDIO_CAPTION: {
      const { episodeAudios } = state;
      return {
        ...state,
        episodeAudios: episodeAudios.map(a => episodeAudioReducer(a, action)),
      };
    }
    case SET_IS_DRAFT: {
      const { isDraft } = action.payload;
      return {
        ...state,
        isDraft,
      };
    }
    case SET_IS_PLAYING: {
      const { isPlaying } = action.payload;
      const activeEditorAudioIndex = isPlaying
        ? null
        : state.activeEditorAudioIndex;
      return {
        ...state,
        isPlaying,
        activeEditorAudioIndex,
      };
    }
    case SET_IS_UPDATING: {
      const { isUpdating } = action.payload;
      return {
        ...state,
        isUpdating,
      };
    }
    case SET_AWAITING_AUTO_SAVE: {
      const { awaitingAutoSave } = action.payload;
      return {
        ...state,
        awaitingAutoSave,
      };
    }
    case RECEIVE_FILE_PROCESSED_PERCENT: {
      const { fileProcessedPercent } = action.payload;
      return {
        ...state,
        fileProcessedPercent,
        addAudioError: null,
      };
    }
    case RECEIVE_ACTIVE_EPISODE_AUDIO_INDEX: {
      const { episodeAudios } = state;
      let { activeEpisodeAudioIndex } = action.payload;
      let activeEpisodeAudio = episodeAudios[activeEpisodeAudioIndex];
      // special case where segment is an ad or otherwise broken, try next one
      if (
        !activeEpisodeAudio ||
        !activeEpisodeAudio.duration ||
        !activeEpisodeAudio.url
      ) {
        activeEpisodeAudioIndex = getNextActiveIndex(
          activeEpisodeAudioIndex,
          episodeAudios
        );
        activeEpisodeAudio = episodeAudios[activeEpisodeAudioIndex];
      }
      return {
        ...state,
        activeEpisodeAudioIndex,
        activeEpisodeAudio,
        playedDuration: playedDurationFromEpisodeAudiosAndActiveIndex(
          episodeAudios,
          activeEpisodeAudioIndex
        ),
      };
    }
    case RECEIVE_ACTIVE_EDITOR_AUDIO_INDEX: {
      const { activeEditorAudioIndex } = action.payload;
      // Audio state from editor is kept locally in the AvailableSegment components
      // This may not be the best way to do this, as the state is reset when the tab is switched
      return {
        ...state,
        activeEditorAudioIndex,
        isPlaying: false,
      };
    }
    case STOP_EDITOR_AUDIO: {
      return {
        ...state,
        activeEditorAudioIndex: -1,
      };
    }
    case RECEIVE_EPISODE_FETCH_REQUEST: {
      return {
        ...state,
        isFetchingEpisode: true,
      };
    }
    case RECEIVE_LOCAL_EPISODE_IMAGE_EDIT: {
      return {
        ...state,
        editedImage: action.payload.imageUrl,
      };
    }
    case RECEIVE_DELETE_SEGMENT_PROMPT: {
      return {
        ...state,
        requestDeleteAudioId: action.payload.audioId,
      };
    }
    case SET_DO_ABANDON_CHANGES: {
      return {
        ...state,
        doAbandonChanges: action.payload.doAbandonChanges,
      };
    }
    case SET_IS_HOVERING_OVER_ADD_BUTTON: {
      return {
        ...state,
        isHoveringOverAddButton: action.payload.isHoveringOverAddButton,
      };
    }
    case SET_IS_USING_SHORTCUT_UPLOAD_FLOW: {
      return {
        ...state,
        isUsingShortcutUploadFlow: action.payload,
      };
    }
    default:
      return state;
  }
}

// action creators

export function addAudioToEpisode(audio, idx) {
  return {
    type: ADD_AUDIO_TO_EPISODE,
    payload: {
      audio,
      idx,
    },
  };
}
export function setIsShowingSponsorshipsMusicErrorModal(
  isShowingSponsorshipsMusicErrorModal
) {
  return {
    type: SET_IS_SHOWING_SPONSORSHIPS_MUSIC_ERROR_MODAL,
    payload: {
      isShowingSponsorshipsMusicErrorModal,
    },
  };
}

export function setIsShowingAnchorFirstAdModal(isShowingAnchorFirstAdModal) {
  return {
    type: SET_IS_SHOWING_ANCHOR_FIRST_AD_MODAL,
    payload: {
      isShowingAnchorFirstAdModal,
    },
  };
}

export function setIsShowingPublishNewEpisodeScreen(
  isShowingPublishNewEpisodeScreen
) {
  return {
    type: SET_IS_SHOWING_PUBLISH_NEW_EPISODE_SCREEN,
    payload: {
      isShowingPublishNewEpisodeScreen,
    },
  };
}

export function setIsShowingActivateSponsorshipsConfirmationModal(
  isShowingActivateSponsorshipsConfirmationModal
) {
  return {
    type: SET_IS_SHOWING_ACTIVATE_SPONSORSHIPS_CONFIRMATION_MODAL,
    payload: {
      isShowingActivateSponsorshipsConfirmationModal,
    },
  };
}

export function setAutoPairedCampaign(autoPairedCampaign) {
  return {
    type: SET_AUTO_PAIRED_CAMPAIGN,
    payload: {
      autoPairedCampaign,
    },
  };
}

function replaceAudioInEpisode(urlToReplace, audio) {
  return {
    type: REPLACE_AUDIO_IN_EPISODE,
    payload: {
      urlToReplace,
      audio,
    },
  };
}

function addAudioToEpisodeError({ title, subTitle, message, isModal = false }) {
  const error = new Error(message);
  error.title = title;
  error.subTitle = subTitle;
  error.isModal = isModal;
  return {
    type: ADD_AUDIO_TO_EPISODE,
    payload: error,
    error: true,
  };
}

export function resetPodcastEpisode() {
  return {
    type: RESET_PODCAST_EPISODE,
  };
}

export function receiveLocalUploadedImage(imageUrl) {
  return {
    type: RECEIVE_LOCAL_EPISODE_IMAGE_EDIT,
    payload: { imageUrl },
  };
}

function receivePodcastEpisode({
  created,
  createdUnixTimestamp,
  description,
  doPublishExternally,
  episodeAudios,
  episodeImage,
  isDraft,
  shareLinkPath,
  shareLinkEmbedPath,
  podcastId,
  podcastEpisodeId,
  podcastEpisodeIsExplicit,
  podcastEpisodeNumber,
  podcastEpisodeType,
  podcastSeasonNumber,
  publishOn,
  safeDescription,
  status,
  title,
}) {
  let metadata = {
    created,
    createdUnixTimestamp,
    description,
    episodeImage,
    podcastEpisodeIsExplicit,
    podcastEpisodeNumber,
    podcastEpisodeType,
    podcastSeasonNumber,
    safeDescription,
    title,
  };
  if (Object.values(metadata).filter(v => !!v).length === 0) {
    metadata = null;
  }
  return {
    type: RECEIVE_PODCAST_EPISODE,
    payload: {
      doPublishExternally,
      episodeAudios,
      isDraft,
      isFetchingEpisode: false,
      metadata,
      podcastId,
      podcastEpisodeId,
      publishOn,
      shareLinkPath,
      shareLinkEmbedPath,
      status,
    },
  };
}

export function changeEpisodeSort({ startSort, endSort }) {
  return {
    type: CHANGE_EPISODE_SORT,
    payload: {
      startSort,
      endSort,
    },
  };
}

function setIsUpdating(isUpdating) {
  return {
    type: SET_IS_UPDATING,
    payload: {
      isUpdating,
    },
  };
}

export function setAwaitingAutoSave(awaitingAutoSave) {
  return {
    type: SET_AWAITING_AUTO_SAVE,
    payload: {
      awaitingAutoSave,
    },
  };
}

export function setIsDraft(isDraft) {
  return {
    type: SET_IS_DRAFT,
    payload: {
      isDraft,
    },
  };
}

function receiveFileProcessedPercent(fileProcessedPercent) {
  return {
    type: RECEIVE_FILE_PROCESSED_PERCENT,
    payload: {
      fileProcessedPercent,
    },
  };
}

function setDoAbandonChanges(doAbandonChanges) {
  return {
    type: SET_DO_ABANDON_CHANGES,
    payload: {
      doAbandonChanges,
    },
  };
}

export function receiveActiveEpisodeAudioIndex(activeEpisodeAudioIndex) {
  return {
    type: RECEIVE_ACTIVE_EPISODE_AUDIO_INDEX,
    payload: {
      activeEpisodeAudioIndex,
    },
  };
}

export function receiveActiveEditorAudioIndex(activeEditorAudioIndex) {
  return {
    type: RECEIVE_ACTIVE_EDITOR_AUDIO_INDEX,
    payload: {
      activeEditorAudioIndex,
    },
  };
}

export function setIsPlaying(isPlaying) {
  return {
    type: SET_IS_PLAYING,
    payload: {
      isPlaying,
    },
  };
}

export function endEpisodeAudio() {
  return {
    type: END_EPISODE_AUDIO,
  };
}

export function receiveDeleteSegmentPrompt(audioId) {
  return {
    type: RECEIVE_DELETE_SEGMENT_PROMPT,
    payload: {
      audioId,
    },
  };
}

export function receiveEpisodeAudioCaption(audio) {
  return {
    type: RECEIVE_EPISODE_AUDIO_CAPTION,
    payload: audio,
  };
}

export function receiveEpisodeFetchRequest() {
  return {
    type: RECEIVE_EPISODE_FETCH_REQUEST,
  };
}

export function removeAudioInEpisode(audioIndex) {
  return {
    type: REMOVE_AUDIO_IN_EPISODE,
    payload: { audioIndex },
  };
}

export function stopEditorAudio() {
  return {
    type: STOP_EDITOR_AUDIO,
  };
}

export function dismissAudioError() {
  return {
    type: DISMISS_AUDIO_ERROR,
  };
}

export function receiveIsHoveringOnAddButton(isHoveringOverAddButton) {
  return {
    type: SET_IS_HOVERING_OVER_ADD_BUTTON,
    payload: { isHoveringOverAddButton },
  };
}

function trackEpisodeMetadataFormSubmit(publishOn, isDraft) {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-episode-editor-submit',
        payload: {
          target: publishOn ? 'Schedule' : isDraft ? 'Save Draft' : 'Publish',
          value: publishOn && publishOn.unix && publishOn.unix(),
        },
      },
    },
  };
}

export function setIsUsingShortcutUploadFlow(isUsingShortcutUploadFlow) {
  return {
    type: SET_IS_USING_SHORTCUT_UPLOAD_FLOW,
    payload: isUsingShortcutUploadFlow,
  };
}

export function setIsAdStateEnabled(isAdStateEnabled) {
  return {
    type: SET_IS_AD_STATE_ENABLED,
    payload: isAdStateEnabled,
  };
}

// thunks

export const updateEpisodeMetadata = ({
  data,
  formName,
  formContainer,
}) => dispatch => {
  dispatch(startSubmit(formName));
  dispatch(
    submitUpdateEpisodeMetadataForm({ ...data, doPublishExternally: true }) // always publish everywhere, product decision
  ).then(() => {
    dispatch(stopSubmit(formName));
    AnchorAPI.fetchPodcastStatus().then(responseJSON => {
      const { isDraft } = data;
      // show the pre-distribution modal if...
      if (
        !responseJSON.podcastStatus && // hasn't gone through the distribution flow already
        formContainer === 'EpisodeEditorPublishContainer' && // is creating a brand new episode
        !isDraft // is publishing an episode
      ) {
        dispatch(preDistributionModalScreenDuckOperations.setIsShowing(true));
      }
    });
  });
};

export const setPublishOnValue = (formName, value) => dispatch => {
  dispatch(change(formName, 'publishOn', value));
};

export const updateSponsorshipsFeaturePreference = sponsorshipsFeaturePreference => (
  dispatch,
  getState
) => {
  const {
    global: {
      podcast: {
        podcast: { webStationId },
      },
    },
  } = getState();
  return AnchorAPI.updateSponsorshipsPreference({
    webStationId,
    sponsorshipsFeaturePreference,
  });
};

export const activateSponsorshipsForFirstTime = ({
  isUpdating,
  podcastEpisodeId,
}) => (dispatch, getState) => {
  const {
    global: {
      podcast: {
        podcast: { webStationId },
      },
    },
    money: {
      status: { hasAuthenticatedStripe },
    },
  } = getState();
  return AnchorAPI.acceptMonetizationTerms(webStationId).then(() => {
    const isAdSlotEnabled = true;
    dispatch(setIsAdStateEnabled(isAdSlotEnabled));
    dispatch(updateSponsorshipsFeaturePreference('accepted'));
    if (!podcastEpisodeId) return null;
    return AnchorAPI.updateEpisodeAdState({
      isAdSlotEnabled,
      webEpisodeId: podcastEpisodeId,
    })
      .then(responseJSON => {
        const { isAutoPairedWithSponsor, adCampaignId } = responseJSON;
        const shouldLaunchAutoPairingModal =
          isAdSlotEnabled && isAutoPairedWithSponsor;
        dispatch(globalPodcastDuckOperations.fetchPodcastAndSet());
        // Prevent losing uploads/etc. The slot will change into a proper ad slot toggle once processing finishes
        if (!isUpdating) {
          dispatch(fetchPodcastEpisode(podcastEpisodeId));
        }
        if (shouldLaunchAutoPairingModal) {
          AnchorAPI.fetchAdCampaignForStation(adCampaignId, webStationId).then(
            responseJSON => {
              const {
                adCampaigns: adCampaignsJSON,
                audios: audiosJSON,
              } = responseJSON;
              const campaignJSON = adCampaignsJSON[0];
              const campaign = SponsorshipsDecoder.decodeAdCampaignJSONIntoCampaign(
                campaignJSON,
                audiosJSON
              );
              const campaignForAdRecording = SponsorshipsDecoder.decodeCampaignIntoAdRecordingModalCampaign(
                campaign,
                hasAuthenticatedStripe
              );
              dispatch(globalPodcastDuckOperations.fetchPodcastAndSet());
              // Prevent losing uploads/etc. The slot will change into a proper ad slot toggle once processing finishes
              if (!isUpdating) {
                dispatch(fetchPodcastEpisode(podcastEpisodeId));
              }
              dispatch(setAutoPairedCampaign(campaignForAdRecording));
              dispatch(setIsShowingAnchorFirstAdModal(true));
              return null;
            }
          );
        }
        return null;
      })
      .catch(err => {
        if (err instanceof AnchorAPIError) {
          const {
            response: { status: statusCode },
          } = err;
          if (statusCode === 451) {
            dispatch(setIsShowingSponsorshipsMusicErrorModal(true));
          }
        }
        // reset because of failed fetch
        dispatch(setIsAdStateEnabled(!isAdSlotEnabled));
      });
  });
};

export function removeAudioWithIdAndReplaceWithAudios(audioId, audios) {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    const { episodeAudios } = episodeEditorScreen;
    const indexOfAudioToReplace = episodeAudios.findIndex(
      episodeAudio => episodeAudio.audioId === audioId
    );

    return new Promise((resolve, reject) => {
      dispatch(removeAudioInEpisode(indexOfAudioToReplace));
      audios.forEach((audio, index) => {
        const audioIndex = indexOfAudioToReplace + index;
        dispatch(addAudioToEpisode(audio, audioIndex));
      });
      // TODO: Maybe we should create another function called `savePodcastEpisode`?
      dispatch(autoSavePodcastEpisode()).then(episodeJson => {
        const { podcastEpisodeId: episodeWebId } = episodeJson;
        const pollingIntervalForCheckingIfAllAudiosHaveUrl = 1000;
        const intervalId = setInterval(() => {
          dispatch(fetchPodcastEpisode(episodeWebId)).then(() => {
            const { episodeEditorScreen } = getState();
            const { episodeAudios } = episodeEditorScreen;
            const episodeIsAwaitingTransformationOnAnAudio = episodeAudios.some(
              audio => audio.url === null && !getIsAudioAdSlot(audio)
            );
            if (!episodeIsAwaitingTransformationOnAnAudio) {
              resolve(episodeEditorScreen);
              clearInterval(intervalId);
            }
          });
        }, pollingIntervalForCheckingIfAllAudiosHaveUrl);
      });
    });
  };
}

export function confirmRejectChanges() {
  return (dispatch, getState) => {
    dispatch(setAwaitingAutoSave(null));
    dispatch(setIsUpdating(false));
    dispatch(setDoAbandonChanges(true));
  };
}

export function triggerAwaitingAutoSave(doForce = false) {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    const { awaitingAutoSave, isUpdating } = episodeEditorScreen;
    // do not try to auto save if the local client is updating (e.g. uploading a file)
    if ((!isUpdating || doForce) && awaitingAutoSave) {
      dispatch(setIsUpdating(true));
      // 1. actually auto-save
      return dispatch(autoSavePodcastEpisode())
        .then(() => {
          const { episodeEditorScreen } = getState();
          // 2. clear queue if another has not been enqueued
          if (awaitingAutoSave === episodeEditorScreen.awaitingAutoSave) {
            dispatch(setAwaitingAutoSave(null));
            return Promise.resolve();
          }
          return dispatch(triggerAwaitingAutoSave());
        })
        .finally(() => {
          dispatch(setIsUpdating(false)); // free UI
          return doForce ? Promise.resolve() : Promise.delay(5000); // but delay dequeue of next autosave
        });
    }
    return Promise.resolve();
  };
}

export function updateAudioSegmentCaption(audio) {
  const { audioId, caption, type } = audio;
  return (dispatch, getState) => {
    // Eager update
    const audioToUpdate = {};
    audioToUpdate[audioId] = { caption, type };
    dispatch(updateAudios(audioToUpdate));
    return fetch(`/api/sourceaudio/caption/${audioId}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ caption }),
    }).then(
      response =>
        // if (response.status === 200) {
        // } else {
        //   // TODO: fail undo
        // }
        response
    );
  };
}

export function deleteAudioSegment() {
  // Redux store receives the requested audio segment; this action takes the state in the store
  // and deletes that audioId
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    const { requestDeleteAudioId } = episodeEditorScreen;
    if (requestDeleteAudioId !== null) {
      return fetch(`/api/sourceaudio/${requestDeleteAudioId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      }).then(response => {
        dispatch(receiveDeleteSegmentPrompt(null));
        return response;
      });
      // warning: this fetch response is handled by EpisodeEditor/index.js
      // because it refetches the feeds after the fact
    }
  };
}

export function fetchPodcastEpisode(podcastEpisodeId) {
  return (dispatch, getState) => {
    dispatch(receiveEpisodeFetchRequest());
    return fetch(`/api/podcastepisode/${podcastEpisodeId}`, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        if (response.status === 401) {
          // not logged in
          dispatch(unsetAndRedirectUser());
          return;
        }
        // e.g. no permissions (403)
        dispatch(push('/404'));
      })
      .then(responseJson => {
        dispatch(receivePodcastEpisode(responseJson));
        return responseJson;
      });
  };
}

// note - no longer using form
export function updatePodcastEpisode() {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    const data = {
      doPublishExternally: episodeEditorScreen.doPublishExternally,
      episodeAudios: episodeEditorScreen.episodeAudios,
      isDraft: episodeEditorScreen.isDraft,
      publishOn: episodeEditorScreen.publishOn,
    };
    dispatch(setIsUpdating(true));
    return createOrUpdatePodcastEpisodeOnServer(
      data,
      episodeEditorScreen.podcastEpisodeId
    )
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        if (response.status === 401) {
          // not logged in
          dispatch(unsetAndRedirectUser());
          return;
        }
        // e.g. no permissions (403)
        dispatch(push('/404'));
      })
      .then(responseJson => {
        dispatch(receivePodcastEpisode(responseJson));
        return responseJson;
      })
      .then(() => {
        dispatch(setAwaitingAutoSave(null));
        // async so that updating flag will be set before transition
        nextFrame(() => {
          // back to episode details
          dispatch(
            push(`/dashboard/episode/${episodeEditorScreen.podcastEpisodeId}`)
          );
        });
      })
      .finally(() => {
        dispatch(reduxFormActions.reset(EPISODE_EDIT_FORM));
        dispatch(setIsUpdating(false));
      });
  };
}

export function changeEpisodeSortWithDelay(args) {
  return (dispatch, getState) => {
    dispatch(changeEpisodeSort(args));
    nextFrame(() => {
      const { episodeEditorScreen } = getState();
      const awaitingAutoSave = Date.now();
      dispatch(setAwaitingAutoSave(awaitingAutoSave));
      if (episodeEditorScreen.isDraft) {
        if (!episodeEditorScreen.awaitingAutoSave) {
          dispatch(triggerAwaitingAutoSave(false)); // force a auto save if empty queue
        }
      }
    });
  };
}

export function dispatchWithAutoSave(actionToDispatch) {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    const awaitingAutoSave = Date.now();
    dispatch(actionToDispatch);
    dispatch(setAwaitingAutoSave(awaitingAutoSave));
    if (episodeEditorScreen.isDraft) {
      if (!episodeEditorScreen.awaitingAutoSave) {
        dispatch(triggerAwaitingAutoSave(false)); // force a auto save if empty queue
      }
    }
  };
}

function autoSavePodcastEpisode() {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    // note: only need to run the most recent / only need one in queue
    const data = {
      doPublishExternally: false,
      episodeAudios: episodeEditorScreen.episodeAudios,
      isDraft: episodeEditorScreen.isDraft,
      publishOn: episodeEditorScreen.publishOn,
    };
    return createOrUpdatePodcastEpisodeOnServer(
      data,
      episodeEditorScreen.podcastEpisodeId
    )
      .then(response => {
        if (response.status === 200) {
          // TODO: 'saved!' alert
          return response.json();
        }
      })
      .then(responseJson => {
        dispatch(receivePodcastEpisode(responseJson));
        return responseJson;
      });
  };
}

export function getLocalAudio(file) {
  return new Promise((resolve, reject) => {
    nextFrame(() => {
      const blob = getBlobUrlCreator();
      if (!blob) {
        reject(new Error('Blob doesnt exist'));
      }
      const url = blob.createObjectURL(file);
      // create an element in memory to get duration
      const audio = document.createElement('audio');
      audio.addEventListener('canplaythrough', evt => {
        // // Chrome web recording bug
        // if (audio.duration === Infinity || audio.duration === NaN) {
        //   audio.duration = fallbackDuration;
        // }

        const duration = (audio.duration || 0) * 1000;
        resolve({
          duration,
          url,
        });
      });
      audio.addEventListener('error', reject);
      audio.src = url;
    });
  });
}

export function uploadFile(
  file,
  { doShowProgress = true, isWebRecording = false } = {}
) {
  return (dispatch, getState) => {
    const {
      user: {
        user: { userId },
      },
    } = getState();
    const formData = new FormData();
    let uploadPath = '/api/audio/file';
    if (isWebRecording) {
      uploadPath = '/api/audio/recording';
      const fileType = file.type === 'audio/wav' ? 'wav' : 'webm';
      const fileName = `blob-${Date.now()}.${fileType}`;
      formData.append('file', file, fileName);
      formData.append('userId', userId);
      formData.append('fileName', fileName);
    } else {
      formData.append('file', file, file.name.replace(/[^a-zA-Z0-9-\.]/g, '-')); // unix-safe file path name
      formData.append('caption', file.name.replace(/\.[^/.]+$/, '')); // remove extension
    }

    // initial feedback
    doShowProgress && dispatch(receiveFileProcessedPercent(1));
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      if (doShowProgress) {
        xhr.upload.addEventListener(
          'progress',
          evt => {
            if (evt.lengthComputable) {
              const percentComplete = evt.loaded / evt.total;
              dispatch(
                receiveFileProcessedPercent(parseInt(100 * percentComplete), 10)
              );
            }
          },
          false
        );
      }
      xhr.open('POST', uploadPath);

      xhr.onload = function onXhrLoad(data) {
        doShowProgress && dispatch(receiveFileProcessedPercent(100));
        resolve(xhr);
      };

      // TODO: new error state. (usually CORS problem)
      xhr.onerror = reject;

      xhr.send(formData);
    })
      .then(xhr => {
        if (xhr.status === 200) {
          try {
            return JSON.parse(xhr.response);
          } catch (err) {
            return Promise.reject(err);
          }
        }
        dispatch(receiveFileProcessedPercent(null));
        if (xhr.status === 401) {
          // not logged in
          dispatch(unsetAndRedirectUser());
          return;
        }
        return Promise.reject(xhr);
      })
      .then(responseJson => responseJson);
  };
}

export function goToEditMetadataForm() {
  return (dispatch, getState) => {
    dispatch(push('/dashboard/episode/new/publish'));
  };
}

/**
 * We only accept a single file via upload, but inputs are arrays
 */
export function receiveAudioFiles(acceptedFiles, rejectedFiles, newIndex) {
  return (dispatch, getState) => {
    const isFileTooLarge = file => file.size > MAX_FILE_SIZE.size;
    const isAudioMimeType = mimetype => mimetype.slice(0, 6) === 'audio/';
    const isMp4MimeType = mimetype => mimetype === 'video/mp4';
    const isAcceptedMimeType = mimetype =>
      isAudioMimeType(mimetype) || isMp4MimeType(mimetype);
    const isFileOfValidType = file => isAcceptedMimeType(file.type);
    const filesRejectedDueToSize = acceptedFiles.filter(acceptedFile =>
      isFileTooLarge(acceptedFile)
    );
    const filesRejectedDueToType = acceptedFiles.filter(
      acceptedFile => !isFileOfValidType(acceptedFile)
    );
    const filesRejectedDueToUnknownReason = rejectedFiles;
    const hasRejectedFilesDueToType = filesRejectedDueToType.length > 0;
    const hasRejectedFilesDueToSize = filesRejectedDueToSize.length > 0;
    const hasRejectedFilesDueToUnknownReason =
      filesRejectedDueToUnknownReason.length > 0;

    if (hasRejectedFilesDueToType) {
      dispatch(
        addAudioToEpisodeError({
          title: 'Upload Error',
          message: filesRejectedDueToType.map(f => f.name).join(', '),
          subTitle:
            'The file you tried to upload was invalid. Please check the file type and try again.',
        })
      );
      return Promise.reject(new Error('Rejection due to file type'));
    }
    if (hasRejectedFilesDueToSize) {
      dispatch(
        addAudioToEpisodeError({
          title: 'Upload Error',
          message: filesRejectedDueToSize.map(f => f.name).join(', '),
          subTitle:
            'This file is too big. Try uploading something less than 250MB.',
        })
      );
      return Promise.reject(new Error('File rejected due to size'));
    }
    if (hasRejectedFilesDueToUnknownReason) {
      dispatch(
        addAudioToEpisodeError({
          title: 'Upload Error',
          message: filesRejectedDueToUnknownReason.map(f => f.name).join(', '),
          subTitle: 'The file you tried to upload was invalid.',
        })
      );
      return Promise.reject(new Error('File rejected due to unknown issue'));
    }

    dispatch(setDoAbandonChanges(false));
    dispatch(setIsUpdating(true)); // disable UI

    return Promise.map(
      acceptedFiles,
      (file, index) => processFile(file, newIndex + index),
      {
        concurrency: 5,
      }
    );

    function processFile(file, index) {
      return getLocalAudio(file)
        .then(localAudio => {
          const now = new Date();
          dispatch(
            addAudioToEpisode(
              {
                ...localAudio,
                audioId: now.getTime(), // fake
                color: COLORS.UPLOAD_FROM_WEB,
                caption: file.name.replace(/\.[^/.]+$/, ''), // remove extension
                created: now.toString(),
                isUploading: true,
              },
              index
            )
          );
          return localAudio;
        })
        .catch(() =>
          // no blob / preview support
          ({})
        )
        .then(localAudio => uploadFileAndAddToEpisode(file, localAudio.url))
        .catch(err => {
          if (err.status >= 500) {
            dispatch(
              addAudioToEpisodeError({
                title: 'Upload failed',
                subTitle:
                  'Something went wrong with your upload. Please try again. If you’re uploading a really large file (>100MB), you may want to compress it into a smaller format, such as mp3. If you need any help troubleshooting, please email us anytime at help@anchor.fm!',
                isModal: true,
              })
            );
            // propagate promise
            return Promise.reject(err);
          }
        })
        .finally(() => {
          garbageCollect(file);
          dispatch(setIsUpdating(false)); /* re-enable UI */
        });
    }

    function garbageCollect(file) {
      // garbage collect
      window.URL.revokeObjectURL(file.preview);
    }

    function uploadFileAndAddToEpisode(file, urlToReplace) {
      const baseAudio = {
        isUploading: false,
      };
      return dispatch(uploadFile(file))
        .then(response => {
          if (response.audio) {
            // TODO: make this type return correctly in the controller
            const uploadSegment = {
              ...response.audio,
              ...baseAudio,
            };
            const { episodeEditorScreen } = getState();
            if (episodeEditorScreen.doAbandonChanges) {
              // abort, user transitioned away
              if (urlToReplace) {
                cleanUpUrl(urlToReplace);
              }
              dispatch(setDoAbandonChanges(false));
              return;
            }
            // note autosave
            if (urlToReplace) {
              nextFrame(() => {
                // allows setIsUpdating(false) to settle
                dispatch(
                  dispatchWithAutoSave(
                    replaceAudioInEpisode(urlToReplace, uploadSegment)
                  )
                );
              });
              return;
            }
            nextFrame(() => {
              // allows setIsUpdating(false) to settle
              dispatch(dispatchWithAutoSave(addAudioToEpisode(uploadSegment)));
            });
          }
        })
        .catch(err => {
          // clean up episode / delete temp file
          if (urlToReplace) {
            cleanUpUrl(urlToReplace);
          }
          // pass through error
          return Promise.reject(err);
        });

      function cleanUpUrl(urlToReplace) {
        const { episodeAudios } = getState().episodeEditorScreen;
        const idx = episodeAudios.findIndex(a => a.url === urlToReplace);
        if (idx !== -1) {
          dispatch(removeAudioInEpisode(idx));
        }
      }
    }
  };
}

/**
 *
 * @param {float} position between 0 and 1
 */
export function updateEpisodePlaybackPosition(position) {
  return (dispatch, getState) => {
    const { episodeDuration, episodeAudios } = getState().episodeEditorScreen;
    const positionInMs = position * episodeDuration;
    const {
      audioIndex,
      audioPositionInMs,
    } = getSeekAudioIndexFromEpisodeAudiosAndPositionInMs(
      episodeAudios,
      positionInMs
    );
    dispatch(receiveActiveEpisodeAudioIndex(audioIndex));
    dispatch(updatePlaybackPositionInMs(audioPositionInMs, true));
  };
}

// misc
function episodeDurationFromEpisodeAudios(episodeAudios = []) {
  // duration is `null` for inactive ad slots
  return episodeAudios.reduce(
    (acc, curr) => acc + (curr.duration != null ? curr.duration : 0),
    0
  );
}

function playedDurationFromEpisodeAudiosAndActiveIndex(
  episodeAudios = [],
  activeIndex = 0
) {
  return episodeDurationFromEpisodeAudios(episodeAudios.slice(0, activeIndex));
}

// important that this returns a promise to the form submit handler
export function submitUpdateEpisodeMetadataForm(formData) {
  return (dispatch, getState) => {
    const { episodeEditorScreen, podcast, podcastEditor } = getState();
    const data = {
      ...formData,
      episodeAudios: episodeEditorScreen.episodeAudios,
    };
    if (!data.title) {
      data.title = getDefaultTitleFromPodcast({ podcast });
    }
    if (!data.description) {
      data.description = getDefaultDescriptionFromPodcast({
        podcast,
        podcastEditor,
      });
    }
    dispatch(trackEpisodeMetadataFormSubmit(data.publishOn, data.isDraft));
    // Create Anchor image first if user edited their episode image
    return new Promise((resolve, reject) => {
      if (episodeEditorScreen.editedImage) {
        const uploadedImageUrl = uploadImageData(
          episodeEditorScreen.editedImage,
          {
            isEpisode: true,
            doOverrideSize: true,
          }
        )
          .then(resolve)
          .catch(reject);
        dispatch(receiveLocalUploadedImage(null));
      } else {
        resolve();
      }
    })
      .then(({ image, image400 } = {}) => {
        if (image) {
          data.episodeImage = image;
          data.episodeImage400 = image400;
        }
        return Promise.resolve();
      })
      .then(() =>
        // look for any awaiting auto saves
        dispatch(triggerAwaitingAutoSave(true)).then(
          () =>
            // TODO: determine if save happened, but for now just delay a frame to let state settle
            new Promise(resolve => {
              nextFrame(resolve);
            })
        )
      )
      .then(() => {
        // see if this has now been set
        const podcastEpisodeId = getState().episodeEditorScreen
          .podcastEpisodeId;
        return createOrUpdatePodcastEpisodeOnServer(
          data,
          podcastEpisodeId,
          true
        );
      })
      .then(response => {
        const { status } = response;
        if (status === 400) {
          throw new SubmissionError({
            _error: 'There was a problem with this title.',
          });
        }
        // e.g. no permissions (403)
        if (status === 403) {
          dispatch(push('/404'));
          return;
        }
        if (status === 401) {
          dispatch(unsetAndRedirectUser());
          return;
        }
        if (status === 500) {
          throw new SubmissionError({
            _error: 'There was a problem; please try again.',
          });
        }
        return response.json();
      })
      .then(responseJson => {
        // pull episode ID from response in case episode was created
        const podcastEpisodeId = responseJson.podcastEpisodeId;
        // clear local editor store
        dispatch(resetPodcastEpisode());
        // back to episode details
        dispatch(
          push(
            `/dashboard/episode/${podcastEpisodeId ||
              episodeEditorScreen.podcastEpisodeId}`
          )
        );
      });
  };
}

export function deletePodcastEpisode() {
  return (dispatch, getState) => {
    const { episodeEditorScreen } = getState();
    return deletePodcastEpisodeOnServer(
      episodeEditorScreen.podcastEpisodeId
    ).then(response => {
      const { status } = response;
      // next frame to allow component state cleanup
      nextFrame(() => {
        // e.g. no permissions (403)
        if (status === 403) {
          dispatch(push('/404'));
          return;
        }
        if (status === 401) {
          dispatch(unsetAndRedirectUser());
          return;
        }
        // back to episode list?
        dispatch(push('/dashboard/episodes'));
      });
    });
  };
}

export function getPodcastEpisodeIdFromProps(props) {
  const { params } = props.routerProps.match;
  return params.podcastEpisodeId;
}

function getHourOffset() {
  // JS API is in minutes
  return new Date().getTimezoneOffset() / 60;
}

function createOrUpdatePodcastEpisodeOnServer(
  data,
  podcastEpisodeId,
  isMetadataOnly = false
) {
  data.hourOffset = getHourOffset();
  if (podcastEpisodeId) {
    if (isMetadataOnly) {
      return new Promise((resolve, reject) => {
        fetch(`/api/podcastepisode/${podcastEpisodeId}/metadata`, {
          method: 'PUT',
          credentials: 'same-origin',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(data),
        })
          .then(resolve)
          .catch(reject);
      });
    }
    return new Promise((resolve, reject) => {
      fetch(`/api/podcastepisode/${podcastEpisodeId}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      })
        .then(resolve)
        .catch(reject);
    });
  }

  return new Promise((resolve, reject) => {
    fetch('/api/podcastepisode', {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    })
      .then(resolve)
      .catch(reject);
  });
}

export function deletePodcastEpisodeOnServer(podcastEpisodeId) {
  return new Promise((resolve, reject) => {
    fetch(`/api/podcastepisode/${podcastEpisodeId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    })
      .then(resolve)
      .catch(reject);
  });
}

export function getDefaultDescriptionFromPodcast({ podcast, podcastEditor }) {
  const episodesLength = podcast.podcastEpisodeTotal;
  if (episodesLength > 1) {
    return `Latest episode of ${podcastEditor.podcastName || 'this podcast!'}`;
  }
  return `The very first episode of ${podcastEditor.podcastName ||
    'this podcast!'}!`;
}

export function getDefaultTitleFromPodcast({ podcast }) {
  const episodesLength = podcast.podcastEpisodeTotal;
  return `Episode ${episodesLength + 1}`;
}

export function isAnySegmentUploading(segments = []) {
  return segments.some(s => s.isUploading);
}

function getNextActiveIndex(activeEpisodeAudioIndex, episodeAudios) {
  if (activeEpisodeAudioIndex === -1) {
    return -1;
  }
  const nextIndexStart = activeEpisodeAudioIndex + 1;
  const remainingAudios = episodeAudios.slice(nextIndexStart);
  const nextIndexFound = remainingAudios.findIndex(
    a => a && a.duration && a.url
  );
  if (nextIndexFound !== -1) {
    return nextIndexFound + nextIndexStart;
  }
  return -1;
}
